import styled, { css } from 'styled-components';

const finalTheme = css`
	--pagination-bg: #f9f9f9;
	--pagination-border: #ced4da;
	--pagination-text: #34495e;
	--pagination-button-bg: #ffffff;
	--pagination-button-hover-bg: #f1f3f5;
	--pagination-button-hover-border: #adb5bd; 
	--pagination-current-bg: #7d848c;
	--pagination-current-text: #ffffff;
	--pagination-disabled-bg: #e9ecef;
	--pagination-disabled-text: #adb5bd;
	--pagination-arrow-bg: #ced2d7;
	--pagination-arrow-text: #ffffff;
	--pagination-arrow-hover-bg: #818991;
`;

export const Controls = styled.div`
	${finalTheme}

	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: flex-end;
	gap: 16px;
	row-gap: 8px;
	background: var(--pagination-bg);
	padding: 8px 16px;
	border-radius: 8px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	width: 100%;
	overflow: visible;
	transition: background 0.3s ease;

	@media (max-width: 768px) {
		padding: 8px;
		gap: 8px;
	}
`;

export const SortLabel = styled.span`
	margin-right: 8px;
	color: var(--pagination-text);
	font-size: 0.95em;
	white-space: nowrap;
	user-select: none;
	transition: color 0.3s ease;

	@media (max-width: 768px) {
		margin-right: 4px;
		font-size: 0.9em;
	}
`;

export const SelectControl = styled.select`
	height: 32px;
	padding: 0 8px;
	border: 1px solid var(--pagination-border);
	border-radius: 6px;
	font-size: 0.95em;
	background: var(--pagination-button-bg);
	color: var(--pagination-text);
	cursor: pointer;
	transition: all 0.2s ease;
	min-width: 0;
	flex-shrink: 1;

	&:hover {
		border-color: var(--pagination-button-hover-border);
		background: var(--pagination-button-hover-bg);
	}

	@media (max-width: 768px) {
		height: 32px;
		font-size: 0.9em;
		padding: 0 6px;
		width: auto;
	}

	@media (max-width: 480px) {
		height: 28px;
		font-size: 0.85em;
		padding: 0 4px;
	}
`;
interface ButtonProps {
	$isCurrent?: boolean;
	$isEllipsis?: boolean;
	$isVisible?: boolean;
	$isActive?: boolean;
}

export const ButtonControl = styled.button<ButtonProps>`
	height: 32px;
	width: 32px;
	min-width: 28px;
	padding: 4px;
	border: 1px solid var(--pagination-border);
	border-radius: 6px;
	background: ${({ $isCurrent, $isActive, $isEllipsis }) =>
		$isCurrent
			? 'var(--pagination-current-bg)'
			: $isEllipsis
			? 'var(--pagination-arrow-bg)'
			: $isActive
			? 'var(--pagination-button-hover-bg)'
			: 'var(--pagination-button-bg)'};
	color: ${({ $isCurrent, $isEllipsis }) =>
		$isCurrent
			? 'var(--pagination-current-text)'
			: $isEllipsis
			? 'var(--pagination-arrow-text)'
			: 'var(--pagination-text)'};
	font-size: 0.95em;
	cursor: ${({ $isCurrent }) => ($isCurrent ? 'default' : 'pointer')};
	transition: all 0.2s ease;
	visibility: ${({ $isVisible = true }) =>
		$isVisible ? 'visible' : 'hidden'};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 1;

	&:hover:not(:disabled) {
		border-color: var(--pagination-button-hover-border);
		background: ${({ $isEllipsis }) =>
			$isEllipsis
				? 'var(--pagination-arrow-hover-bg)'
				: 'var(--pagination-button-hover-bg)'};
	}

	&[disabled] {
		background: var(--pagination-disabled-bg);
		color: var(--pagination-disabled-text);
		cursor: default;
		border-color: var(--pagination-border);
		&:hover {
			background: var(--pagination-disabled-bg);
		}
	}

	&[disabled][data-is-current='true'] {
		background: var(--pagination-current-bg);
		color: var(--pagination-current-text);
		border-color: var(--pagination-current-bg);
		&:hover {
			background: var(--pagination-current-bg);
		}
	}

	@media (max-width: 768px) {
		height: 32px;
		width: 32px;
		min-width: 24px;
		font-size: 0.9em;
	}

	@media (max-width: 480px) {
		height: 28px;
		width: 28px;
		min-width: 20px;
		font-size: 0.85em;
		padding: 2px;
	}
`;

export const EllipsisContainer = styled.div`
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 32px;
`;

export const EllipsisDropdown = styled.div`
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	top: 36px;
	background: var(--pagination-bg);
	border: 1px solid var(--pagination-border);
	border-radius: 6px;
	box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
	padding: 6px;
	display: flex;
	flex-direction: column;
	overflow-y: visible;
	z-index: 9999;
	min-width: 40px;
	width: auto;
`;

export const DropdownItem = styled.div`
	background: var(--pagination-button-bg);
	border: none;
	padding: 6px 8px;
	margin: 2px 0;
	height: 34px;
	box-sizing: border-box;
	font-size: 0.95em;
	color: var(--pagination-text);
	cursor: pointer;
	text-align: center;
	border-radius: 4px;
	min-width: 28px;

	&:hover {
		background: var(--pagination-button-hover-bg);
		color: var(--pagination-button-hover-border);
	}

	@media (max-width: 480px) {
		font-size: 0.85em;
		padding: 4px 6px;
	}
`;

export const ArrowButtonControl = styled(ButtonControl)`
	background-color: var(--pagination-arrow-bg);
	border-color: var(--pagination-arrow-bg);
	color: var(--pagination-arrow-text);

	&:hover:not(:disabled) {
		background: var(--pagination-arrow-hover-bg);
		border-color: var(--pagination-arrow-hover-bg);
	}
`;

export const SortControls = styled.div`
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	gap: 16px;
	flex-shrink: 1;
	min-width: 0;
	overflow: hidden;

	@media (max-width: 768px) {
		gap: 8px;
	}

	@media (max-width: 480px) {
		gap: 4px;
	}
`;

export const PaginationControls = styled.div`
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	margin-left: auto;
	flex-shrink: 1;
	min-width: 0;
	overflow: visible;

	@media (max-width: 768px) {
		gap: 4px;
	}

	@media (max-width: 480px) {
		gap: 2px;
	}
`;
